if (!customElements.get('video-hero')) {
  customElements.define(
    'video-hero',
    class VideoHero extends HTMLElement {
      connectedCallback() {
        this.video = this.videoElement || this.querySelector('video');
        if (!this.video) return;

        // Native autoplay can start before the first IntersectionObserver callback.
        const bounds = this.video.getBoundingClientRect();
        this.inViewport = bounds.bottom > 0 && bounds.top < window.innerHeight;
        this.removeAttribute('data-playing');
        this.events = new AbortController();
        const options = { signal: this.events.signal };

        // Keep the background silent, including when Safari restores the page.
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.playsInline = true;
        this.video.controls = false;
        this.video.autoplay = true;
        this.video.loop = true;
        this.video.preload = 'auto';
        this.video.setAttribute('webkit-playsinline', '');
        this.video.setAttribute('loading', 'eager');
        this.video.addEventListener(
          'playing',
          () => {
            if (this.shouldPlay) this.setAttribute('data-playing', '');
            else this.video.pause();
          },
          options
        );
        for (const event of ['pause', 'emptied']) {
          this.video.addEventListener(event, () => this.removeAttribute('data-playing'), options);
        }
        for (const event of ['loadedmetadata', 'loadeddata', 'canplay', 'error']) {
          this.video.addEventListener(event, () => this.syncPlayback(), options);
        }
        document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
        window.addEventListener('pageshow', () => this.syncPlayback(), options);
        // Safari may defer autoplay while the page's reveal animation hides the video.
        const reveal = this.video.closest('.scroll-trigger');
        reveal?.addEventListener('animationend', () => this.syncPlayback(), options);

        // A normal page interaction can unlock playback when mobile autoplay is blocked.
        for (const event of ['touchend', 'pointerup', 'keydown']) {
          document.addEventListener(event, () => this.syncPlayback(), { ...options, passive: true });
        }

        this.observer = new IntersectionObserver(([entry]) => {
          this.inViewport = entry.isIntersecting;
          this.syncPlayback();
        });
        this.observer.observe(this.video);
        this.syncPlayback();
      }

      disconnectedCallback() {
        this.events?.abort();
        this.observer?.disconnect();
        this.video?.pause();
        this.removeAttribute('data-playing');
      }

      get shouldPlay() {
        return this.isConnected && this.video.isConnected && this.inViewport && !document.hidden && !this.video.error;
      }

      syncPlayback() {
        this.toggleAttribute('data-unavailable', !!this.video.error);

        if (this.shouldPlay) {
          // Native autoplay can precede registration of the playing event listener.
          if (!this.video.paused) {
            if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) this.setAttribute('data-playing', '');
            return;
          }

          this.video.muted = true;
          // The separate poster covers native mobile controls until playback starts.
          this.video.play()?.then(
            () => {
              this.removeAttribute('data-autoplay-blocked');
              if (this.shouldPlay) this.setAttribute('data-playing', '');
              else this.video.pause();
            },
            (error) => {
              this.toggleAttribute('data-autoplay-blocked', error.name === 'NotAllowedError');
              if (this.video.paused) this.removeAttribute('data-playing');
            }
          );
        } else {
          this.removeAttribute('data-playing');
          this.video.pause();
        }
      }
    }
  );

  // Apply the same playback lifecycle to videos inserted in the About page's rich text.
  const aboutVideoControllers = new WeakMap();
  const initializeAboutVideos = (root) => {
    root.querySelectorAll('.about-video-pair video').forEach((video) => {
      if (video.closest('video-hero') || aboutVideoControllers.get(video)?.isConnected) return;

      video.autoplay = true;
      video.loop = true;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.controls = false;
      video.preload = 'auto';
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('loading', 'eager');
      video.classList.add('about-video-pair__video');

      // Moving an already-loaded video can interrupt native autoplay on iOS.
      const controller = document.createElement('video-hero');
      controller.videoElement = video;
      controller.hidden = true;
      aboutVideoControllers.set(video, controller);
      video.after(controller);
    });
  };

  initializeAboutVideos(document);
  document.addEventListener('shopify:section:load', (event) => initializeAboutVideos(event.target));
}
